import {
  aqiBand,
  compass,
  fmtClock,
  fmtDuration,
  fmtPrecip,
  fmtSnow,
  fmtTemp,
  fmtWind,
  pickAqi,
  DETAIL_STATS,
  type AirData,
  type CurrentData,
  type DayData,
  type DetailStat,
  type WeatherPrefs
} from '../weather';

interface Props {
  day: DayData | null;
  // Present only for today: switches rows from daily extremes to readings.
  current: CurrentData | null;
  air: AirData | null;
  prefs: WeatherPrefs;
}

// Label/value rows for the statistics chosen in settings.
export default function WeatherDetails({ day, current, air, prefs }: Props) {
  const rows = prefs.detail_stats
    .map((s) => [s, row(s, day, current, air, prefs)] as const)
    .filter((r): r is readonly [DetailStat, string] => r[1] !== null);
  if (rows.length === 0) return null;
  return (
    <dl className="m-0 grid grid-cols-2 gap-x-4 gap-y-2 text-[0.8rem]">
      {rows.map(([stat, value]) => (
        <div key={stat} className="flex min-w-0 flex-col gap-[1px]">
          <dt className="text-[0.7rem] text-[var(--text-faint)]">
            {DETAIL_STATS.find((d) => d.id === stat)?.label}
          </dt>
          <dd className="m-0 tabular-nums leading-snug">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function row(
  stat: DetailStat,
  d: DayData | null,
  c: CurrentData | null,
  air: AirData | null,
  prefs: WeatherPrefs
): string | null {
  const t = prefs.temperature_unit;
  const w = prefs.wind_speed_unit;
  switch (stat) {
    case 'feels_like':
      if (c?.apparent_temperature != null) return fmtTemp(c.apparent_temperature, t);
      if (d?.apparent_temperature_max == null) return null;
      return `${fmtTemp(d.apparent_temperature_max, t)} high, ${fmtTemp(d.apparent_temperature_min, t)} low`;
    case 'humidity':
      return c?.relative_humidity_2m != null ? `${Math.round(c.relative_humidity_2m)}%` : null;
    case 'wind':
      if (c?.wind_speed_10m != null) return `${fmtWind(c.wind_speed_10m, w)} ${compass(c.wind_direction_10m)}`.trim();
      if (d?.wind_speed_10m_max == null) return null;
      return `up to ${fmtWind(d.wind_speed_10m_max, w)} ${compass(d.wind_direction_10m_dominant)}`.trim();
    case 'gusts':
      if (c?.wind_gusts_10m != null) return fmtWind(c.wind_gusts_10m, w);
      return d?.wind_gusts_10m_max != null ? `up to ${fmtWind(d.wind_gusts_10m_max, w)}` : null;
    case 'precip': {
      if (!d) return null;
      const parts: string[] = [];
      if (d.precipitation_sum != null) parts.push(fmtPrecip(d.precipitation_sum, prefs.precipitation_unit));
      if (d.snowfall_sum != null && d.snowfall_sum >= 0.1)
        parts.push(`${fmtSnow(d.snowfall_sum, prefs.precipitation_unit)} snow`);
      if (d.precipitation_probability_max != null)
        parts.push(`${Math.round(d.precipitation_probability_max)}% chance`);
      return parts.length ? parts.join(', ') : null;
    }
    case 'uv': {
      const now = c?.uv_index;
      const peak = d?.uv_index_max;
      if (now != null && peak != null) return `${Math.round(now)} now, peak ${Math.round(peak)}`;
      if (peak != null) return `peak ${Math.round(peak)}`;
      return now != null ? String(Math.round(now)) : null;
    }
    case 'aqi': {
      const v = c ? pickAqi(air, prefs.aqi_scale) : pickAqi({ us_aqi: d?.us_aqi_max, european_aqi: d?.european_aqi_max }, prefs.aqi_scale);
      if (v == null) return null;
      const scale = prefs.aqi_scale === 'us' ? 'US' : 'EU';
      return `${Math.round(v)} ${scale}, ${aqiBand(v, prefs.aqi_scale).toLowerCase()}${c ? '' : ' at worst'}`;
    }
    case 'pm2_5':
      return c && air?.pm2_5 != null ? `${Math.round(air.pm2_5)} µg/m³` : null;
    case 'sun':
      if (d?.sunrise == null) return null;
      return `${fmtClock(d.sunrise)} to ${fmtClock(d.sunset)}`;
    case 'daylight':
      return d?.daylight_duration != null ? fmtDuration(d.daylight_duration) : null;
  }
}
